import { useEffect, useState } from 'react';

/**
 * Floating "back to top" control for long pages.
 *
 * Hidden until the reader is a full viewport down, so it never covers content
 * on a short page and never appears before there is anything to scroll back to.
 *
 * Positioned with env(safe-area-inset-*) padding on the wrapper so it clears the
 * home indicator on notched phones and iPad, and sized at 48px — comfortably
 * past the 44px minimum touch target.
 */
export default function BackToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // rAF-throttled: scroll fires far more often than the screen repaints, and
    // this only ever flips one boolean.
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        setVisible(window.scrollY > window.innerHeight);
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  // Instant, not smooth: on a long page a smooth glide spends seconds flying
  // past content the reader has already decided to leave. This also means there
  // is no motion to suppress for prefers-reduced-motion.
  const toTop = () => window.scrollTo({ top: 0, behavior: 'instant' });

  return (
    <div
      className="pointer-events-none fixed bottom-0 right-0 z-40 p-5"
      style={{
        paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom))',
        paddingRight: 'calc(1.25rem + env(safe-area-inset-right))',
      }}
    >
      <button
        type="button"
        onClick={toTop}
        aria-label="Back to top"
        // aria-hidden while off-screen keeps it out of the tab order and out of
        // a screen reader's element list until it is genuinely available.
        aria-hidden={!visible}
        tabIndex={visible ? 0 : -1}
        // Filled brand gold with a white arrow: a white button on a white page
        // reads as part of the layout, a solid gold disc reads as a control.
        // gold-600 -> gold-700 keeps the arrow at 4.4:1 or better against its
        // own background; the lighter gold steps drop under the 3:1 icons need.
        className={`grid h-12 w-12 place-items-center rounded-full bg-linear-to-b from-gold-600 to-gold-700 text-white ring-1 ring-gold-700/20 shadow-lg shadow-gold-700/30 transition duration-200 ease-out hover:from-gold-500 hover:to-gold-600 hover:shadow-xl hover:shadow-gold-700/35 active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-600 ${
          visible
            ? 'pointer-events-auto translate-y-0 opacity-100'
            : 'pointer-events-none translate-y-3 opacity-0'
        }`}
      >
        <svg
          aria-hidden
          viewBox="0 0 24 24"
          className="h-5 w-5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 19V5" />
          <path d="m5 12 7-7 7 7" />
        </svg>
      </button>
    </div>
  );
}
